import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ProjectContextService } from '@core/services/project-context.service';
import { IssueReportDialogComponent } from './issue-report-dialog.component';
import { captureIssueContext, IssueReceipt } from './issue-report.service';

@Component({
  selector: 'qms-issue-report-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule],
  template: `@if (!dismissed()) {
    <div class="report-control">
      <button mat-flat-button class="report-button" (click)="open()" aria-label="Report issue">
        <mat-icon>feedback</mat-icon><span>Report issue</span>
      </button>
      <button mat-icon-button class="dismiss-button" (click)="dismissed.set(true)"
        aria-label="Dismiss report issue button" matTooltip="Dismiss">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  }`,
  styles: `
    .report-control { position: fixed; right: max(16px, env(safe-area-inset-right)); bottom: max(16px, env(safe-area-inset-bottom)); z-index: 900;
      display: flex; align-items: center; background: #ffffff; color: #424548; border-radius: 6px; box-shadow: 0 3px 12px #0003; }
    .report-button { background: #ffffff; color: #424548; border-radius: 6px 0 0 6px; min-height: 44px; box-shadow: none; }
    .dismiss-button { color: #424548; flex: 0 0 48px; width: 48px; height: 48px; border-left: 1px solid #DBDCDD; border-radius: 0 6px 6px 0; }
    @media (max-width: 600px) { .report-control { bottom: max(72px, env(safe-area-inset-bottom)); } }
  `,
})
export class IssueReportButtonComponent {
  readonly dismissed = signal(false);
  private readonly router = inject(Router);
  private readonly project = inject(ProjectContextService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);

  open(): void {
    const ref = this.dialog.open(IssueReportDialogComponent, {
      width: '720px', maxWidth: 'calc(100vw - 24px)', maxHeight: '90dvh',
      data: captureIssueContext(this.router.url, this.project.currentProjectId()),
      autoFocus: 'first-tabbable', restoreFocus: true,
    });
    ref.afterClosed().subscribe((receipt: IssueReceipt | undefined) => {
      if (receipt) this.snackBar.open(receipt.notificationSent
        ? 'Issue report saved and notification sent.'
        : 'Issue report saved. Email delivery could not be confirmed.', 'Close', { duration: 10000 });
    });
  }
}